import React from 'react';
import { motion } from 'framer-motion';
import { Users, Globe2, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { usePilgrimGuideStore } from '../../stores/pilgrimGuideStore';
import { CountryDistributionChart } from './CountryDistributionChart';
import { useLanguage } from '../../contexts/LanguageContext';

export const LinkingStats = () => {
  const { translations } = useLanguage();
  const { links } = usePilgrimGuideStore();
  const linksList = Object.values(links);

  // Calculate stats
  const stats = React.useMemo(() => {
    const totalLinks = linksList.length;
    const approvedLinks = linksList.filter(link => link.status === 'approved').length;
    const pendingLinks = linksList.filter(link => link.status === 'pending').length;
    const disputedLinks = linksList.filter(link => link.disputeId).length;

    // Calculate total including half of disputes
    const totalWithDisputes = approvedLinks + Math.floor(disputedLinks / 2);

    // Calculate disputes by organizer
    const organizerStats = linksList.reduce((acc, link) => {
      if (link.disputeId) {
        acc[link.organizerName] = (acc[link.organizerName] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    return {
      total: totalWithDisputes, // Updated to include half of disputes
      approved: approvedLinks,
      disputed: disputedLinks,
      byOrganizer: organizerStats,
      countries: new Set(linksList.map(link => link.residenceCountry)).size
    };
  }, [linksList]);

  const statItems = [
    {
      title: translations?.linkingStats?.totalPilgrims || 'Total Pilgrims',
      value: stats.total,
      icon: Users,
      color: 'primary',
    },
    {
      title: translations?.linkingStats?.countries || 'Number of Countries',
      value: stats.countries,
      icon: Globe2,
      color: 'blue',
    },
    {
      title: translations?.linkingStats?.approved || 'Approved Links',
      value: stats.approved,
      icon: CheckCircle,
      color: 'green',
    },
    {
      title: translations?.linkingStats?.disputed || 'Disputed Links',
      value: stats.disputed,
      icon: AlertCircle,
      color: 'red',
    }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {statItems.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`bg-white p-6 rounded-xl shadow-sm ring-1 ring-inset ${
              stat.color === 'primary' ? 'ring-primary-500/20' :
              stat.color === 'blue' ? 'ring-blue-500/20' :
              stat.color === 'green' ? 'ring-green-500/20' :
              stat.color === 'yellow' ? 'ring-yellow-500/20' :
              'ring-red-500/20'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${
                stat.color === 'primary' ? 'bg-primary-50' :
                stat.color === 'blue' ? 'bg-blue-50' :
                stat.color === 'green' ? 'bg-green-50' :
                stat.color === 'yellow' ? 'bg-yellow-50' :
                'bg-red-50'
              }`}>
                <stat.icon className={
                  stat.color === 'primary' ? 'text-primary-500' :
                  stat.color === 'blue' ? 'text-blue-500' :
                  stat.color === 'green' ? 'text-green-500' :
                  stat.color === 'yellow' ? 'text-yellow-500' :
                  'text-red-500'
                } size={24} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">{stat.title}</p>
                <p className={`text-2xl font-bold mt-1 ${
                  stat.color === 'primary' ? 'text-primary-500' :
                  stat.color === 'blue' ? 'text-blue-500' :
                  stat.color === 'green' ? 'text-green-500' :
                  stat.color === 'yellow' ? 'text-yellow-500' :
                  'text-red-500'
                }`}>
                  {stat.value}
                  {stat.title === translations?.linkingStats?.totalPilgrims && stats.disputed > 0 && (
                    <span className="text-sm font-normal ml-2 opacity-75">
                      ({stats.approved} + {Math.floor(stats.disputed / 2)} disputes)
                    </span>
                  )}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <CountryDistributionChart />
      </motion.div>
    </div>
  );
};